import React, { useEffect, useState } from "react";

import {
  Box,
  Alert,
  AlertTitle,
  LinearProgress,
  IconButton,
  ListItemText,
  ListItemAvatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  Popover,
  Typography,
  ListItemButton,
  Paper,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useTheme } from "@mui/material/styles";
import useContract from "../hooks/useContract";
import { ContractProgress, IContract, LoadingState } from "../types/contract";
import CircularProgressWithLabel from "./CircularProgressWithLabel";
import {
  Check,
  ExpandMore as ExpandMoreIcon,
  WarningAmber,
} from "@mui/icons-material";
import { differenceInMinutes, isSameDay } from "date-fns";
import OwlAvatarIcon from "../icons/OwlAvatarIcon";
import AskMeAnything from "../pages/components/AskMeAnything";

export const LoadingStateText = ({
  type,
  loadingState,
  loadingStatePerComponent,
  loadingStatePerPrompt,
}: {
  type: "contract" | "document";
  loadingState?: LoadingState;
  loadingStatePerComponent: Record<string, LoadingState>;
  loadingStatePerPrompt: Record<string, LoadingState>;
}) => {
  const { t } = useTranslation();

  if (!loadingState) {
    return <span />;
  }

  // get the first non completed loading state from the loadingStatePerComponent
  if (loadingState === LoadingState.QUEUED) {
    return <Typography>{t(`${type}.queued`)}</Typography>;
  }

  if (loadingState === LoadingState.READING) {
    // When document is being indexed by Spykio and Azure Document Intelligence
    return <Typography>{t(`${type}.reading`)}</Typography>;
  }

  if (loadingState === LoadingState.COMPLETED) {
    // All done
    return <Typography>{t(`${type}.completed`)}</Typography>;
  }

  if (loadingState === LoadingState.FAILED) {
    return <Typography>{t("Reading failed")}</Typography>;
  }

  if (loadingState === LoadingState.DATA_EXTRACTION) {
    const firstQueuedComponent = Object.entries(loadingStatePerComponent).find(
      ([, state]) => state === LoadingState.QUEUED
    );

    const firstDataExtractionComponent = Object.entries(
      loadingStatePerComponent
    ).find(([, state]) => state === LoadingState.DATA_EXTRACTION);

    // Show any queued component first, only when there are no queued components then show the first data extraction component
    if (firstQueuedComponent) {
      const [component, state] = firstQueuedComponent;
      return (
        <Typography>
          {t(`${type}.queued_component`, { component: t(component) })}
        </Typography>
      );
    } else if (firstDataExtractionComponent) {
      const [component, state] = firstDataExtractionComponent;
      return (
        <Typography>
          {t(`${type}.extracting_component`, { component: t(component) })}
        </Typography>
      );
    } else {
      // all components are completed, check if there are any prompts
      if (
        loadingStatePerPrompt &&
        Object.keys(loadingStatePerPrompt).length > 0
      ) {
        const firstQueuedPrompt = Object.entries(loadingStatePerPrompt).find(
          ([, state]) => state === LoadingState.QUEUED
        );

        if (firstQueuedPrompt) {
          const [prompt, state] = firstQueuedPrompt;
          return (
            <Typography>{t(`${type}.queued_prompt`, { prompt })}</Typography>
          );
        }

        const firstDataExtractionPrompt = Object.entries(
          loadingStatePerPrompt
        ).find(([, state]) => state === LoadingState.DATA_EXTRACTION);

        if (firstDataExtractionPrompt) {
          const [prompt, state] = firstDataExtractionPrompt;
          return (
            <Typography>
              {t(`${type}.extracting_prompt`, { prompt })}
            </Typography>
          );
        }
      }
    }

    return <Typography>{t("Extracting data")}</Typography>;
  }

  return <Typography>{t(`Analyzing ${type}`)}</Typography>;
};

type AnalyzingContractsLoaderProps = {
  contract?: IContract;
  variant?: "circular" | "linear";
};

const AnalyzingContractsLoader: React.FC<AnalyzingContractsLoaderProps> = ({
  contract,
  variant,
}: {
  contract?: IContract;
  variant?: "circular" | "linear";
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const [totalProgress, setTotalProgress] = useState<number>(0);

  const { contractProgresses, isAnalyzing, analyzingNumber, analyzingSuccess } =
    useContract();

  const [analyzingContract, setAnalyzingContract] =
    React.useState<ContractProgress>();
  const [analyzingContracts, setAnalyzingContracts] = React.useState<
    ContractProgress[]
  >([]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  useEffect(() => {
    if (contract?.id) {
      const newAnalyzingContract = contractProgresses.find(
        (c) => c.contractId === contract?.id
      );
      setAnalyzingContract(newAnalyzingContract);
    } else {
      setAnalyzingContract(undefined);
    }
  }, [contract?.id, contractProgresses]);

  useEffect(() => {
    if (contractProgresses?.length > 0) {
      setAnalyzingContracts(contractProgresses);

      if (analyzingContract && analyzingContract?.overallProgress) {
        setTotalProgress(analyzingContract.overallProgress);
      } else if (!analyzingContract) {
        const totalProgress = contractProgresses?.reduce(
          (acc, contractProgress) =>
            acc + (contractProgress?.overallProgress || 0),
          0
        );

        setTotalProgress(totalProgress / contractProgresses.length);
      }
    }
  }, [
    contractProgresses,
    analyzingContract,
    isAnalyzing,
    analyzingNumber,
    analyzingSuccess,
    contract,
  ]);

  const ageInMinutes = analyzingContract?.initiationDate
    ? differenceInMinutes(
        new Date(),
        new Date(analyzingContract.initiationDate)
      )
    : null;

  const finishedToday = analyzingContract?.completionDate
    ? analyzingContract?.overallProgress === 100 &&
      isSameDay(new Date(analyzingContract.completionDate), new Date())
    : false;

  const contractsRemaining = contractProgresses?.filter(
    (c) => !c.overallProgress || c.overallProgress < 100
  );

  const documentsRemaining = contractProgresses?.filter((c) =>
    c.documentsProgress?.some(
      (d) => !d.overallProgress || d.overallProgress < 100
    )
  );

  const contractsCompleted = contractProgresses?.filter(
    (c) => c.overallProgress === 100
  );

  const contractsFailed = contractProgresses?.filter(
    (c) => c.overallProgress === -1
  );

  // if (variant === "circular" && contractsRemaining.length === 0) return null;

  if (
    variant !== "circular" &&
    !analyzingContract &&
    contractsRemaining?.length === 0 &&
    documentsRemaining?.length === 0
  ) {
    // do not show anything if there are no contracts or documents being analyzed
    // except if we are on a contract detail page
    return null;
  }

  const allDocumentsCompleted = analyzingContract?.documentsProgress?.every(
    (doc) => doc.loadingState === LoadingState.COMPLETED
  );

  const body = (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        gap: 4,
        flexDirection: "column",
      }}
    >
      {isAnalyzing ? (
        <>
          {contract ? ( // this will be displayed on the contract detail page
            <>
              {analyzingContract && (
                <>
                  {analyzingContract.documentsProgress?.length === 0 ? (
                    <>
                      <Alert severity="info">
                        <LoadingStateText
                          type="contract"
                          loadingState={analyzingContract.loadingState}
                          loadingStatePerComponent={
                            analyzingContract.loadingStatePerComponent || {}
                          }
                          loadingStatePerPrompt={
                            analyzingContract.loadingStatePerPrompt || {}
                          }
                        />
                      </Alert>

                      <LinearProgress
                        variant="determinate"
                        value={totalProgress}
                      />
                    </>
                  ) : allDocumentsCompleted &&
                    analyzingContract.loadingState ===
                      LoadingState.COMPLETED ? (
                    <Alert
                      severity="success"
                      sx={{
                        width: "100%",
                      }}
                      slotProps={{
                        message: {
                          sx: {
                            width: "100%",
                          },
                        },
                      }}
                    >
                      <AlertTitle>{t("final_validation")}</AlertTitle>
                      {t("final_validation.description")}
                    </Alert>
                  ) : (
                    analyzingContract.documentsProgress?.map(
                      (documentProgress) => (
                        <Alert
                          key={documentProgress.documentId}
                          severity={
                            documentProgress.loadingState ===
                            LoadingState.FAILED
                              ? "error"
                              : documentProgress.loadingState ===
                                LoadingState.COMPLETED
                              ? "success"
                              : "info"
                          }
                          sx={{
                            width: "100%",
                          }}
                          slotProps={{
                            message: {
                              sx: {
                                width: "100%",
                              },
                            },
                          }}
                        >
                          <AlertTitle>{documentProgress.name}</AlertTitle>
                          <LoadingStateText
                            type="document"
                            loadingState={documentProgress.loadingState}
                            loadingStatePerComponent={
                              documentProgress.loadingStatePerComponent || {}
                            }
                            loadingStatePerPrompt={
                              documentProgress.loadingStatePerPrompt || {}
                            }
                          />

                          {documentProgress.loadingState !==
                            LoadingState.QUEUED &&
                            documentProgress.loadingState !==
                              LoadingState.COMPLETED &&
                            documentProgress.loadingState !==
                              LoadingState.FAILED && (
                              <LinearProgress
                                variant={
                                  documentProgress.loadingState ===
                                  LoadingState.READING
                                    ? "indeterminate"
                                    : "determinate"
                                }
                                value={documentProgress.overallProgress}
                              />
                            )}
                        </Alert>
                      )
                    )
                  )}

                  {/* {analyzingContract?.overallProgress &&
                    analyzingContract?.overallProgress >= 0 &&
                    analyzingContract?.overallProgress < 100 && (
                      <LinearProgress
                        variant="determinate"
                        value={totalProgress}
                      />
                    )} */}
                </>
              )}
            </>
          ) : (
            <Accordion sx={{ my: 0, marginBottom: 0 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                {documentsRemaining.length === 0 &&
                contractsFailed.length > 0 ? (
                  <Alert
                    severity="error"
                    sx={{ width: "100%", backgroundColor: "transparent" }}
                  >
                    {t("ContractsNotRead", { count: contractsFailed.length })}
                  </Alert>
                ) : documentsRemaining.length === 0 &&
                  contractsCompleted.length > 0 ? (
                  <Alert
                    severity="success"
                    sx={{ width: "100%", backgroundColor: "transparent" }}
                  >
                    {t("contracts.completed", {
                      count: contractsCompleted.length,
                    })}
                  </Alert>
                ) : (
                  <Grid container direction="column" size={12} gap={6}>
                    {/* {t("Reading", {
                        percentage: Math.round(totalProgress * 10) / 10,
                      })} */}

                    {t("Reading_contracts", {
                      count: contractsRemaining.length,
                    })}

                    {/* <LinearProgressWithLabel
                      variant="determinate"
                      value={totalProgress}
                    /> */}
                  </Grid>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <List disablePadding>
                  {analyzingContracts.map((contractProgress, i) => (
                    <>
                      <ListItemButton
                        key={contractProgress.contractId}
                        onClick={() =>
                          navigate(`/contract/${contractProgress.contractId}`)
                        }
                      >
                        <ListItemAvatar>
                          {contractProgress.overallProgress === -1 ? (
                            <WarningAmber />
                          ) : contractProgress.overallProgress === 100 ? (
                            <Check />
                          ) : (
                            <CircularProgressWithLabel
                              variant={
                                !contractProgress.overallProgress
                                  ? "indeterminate"
                                  : "determinate"
                              }
                              value={contractProgress.overallProgress || 0}
                              size="2rem"
                            />
                          )}
                        </ListItemAvatar>

                        <ListItemText
                          primary={contractProgress.name}
                          secondary={
                            contractProgress.initiationDate &&
                            contractProgress.completionDate
                              ? t("date.ago", {
                                  date:
                                    contractProgress?.overallProgress &&
                                    contractProgress.overallProgress >= -1 &&
                                    contractProgress.overallProgress < 100
                                      ? new Date(
                                          contractProgress.initiationDate
                                        )
                                      : new Date(
                                          contractProgress.completionDate
                                        ),
                                })
                              : undefined
                          }
                        />
                      </ListItemButton>

                      {i < analyzingContracts.length - 1 && (
                        <Divider component="li" />
                      )}
                    </>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      ) : analyzingSuccess ? (
        <Alert severity="success">
          <AlertTitle>{t("Success")}</AlertTitle>
          {t("document.completed")}
        </Alert>
      ) : (
        <span />
      )}

      {/* {analyzeError && (
          <Alert severity="error">
            <AlertTitle>{t("An error occurred!")}</AlertTitle>
            {analyzeError?.data && JSON.stringify(analyzeError.data)}
            {!analyzeError?.data && JSON.stringify(analyzeError)}
          </Alert>
        )} */}
    </Box>
  );

  return (
    <Box>
      {analyzingContracts?.length > 0 && !isNaN(totalProgress) && (
        <>
          {variant === "circular" ? (
            <>
              <IconButton onClick={handleClick}>
                {contractsRemaining.length === 0 &&
                documentsRemaining.length === 0 ? (
                  <OwlAvatarIcon />
                ) : (
                  <CircularProgressWithLabel
                    variant="determinate"
                    value={totalProgress}
                    size="2rem"
                  />
                )}
              </IconButton>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.background.default,
                    gap: 2,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <AskMeAnything
                    placeholder={"Ask a question about your contracts"}
                  />
                  {body}
                </Paper>
              </Popover>
            </>
          ) : (
            body
          )}
        </>
      )}
    </Box>
  );
};

export default AnalyzingContractsLoader;
